/**
 * ResourceTable Component
 * Resource list with sortable columns, status badges, and row actions
 */

import { Edit2, Trash2, Star, ChevronUp, ChevronDown } from 'lucide-react';
import { formatDate } from '@/shared/utils/date-formatter';
import { type AdminResource, getAuthorProDisplayName } from '../types/admin-resource.type';
import { ResourceTableSkeleton } from './ResourceTableSkeleton';

type ResourceTableProps = {
  resources: AdminResource[];
  totalCount: number;
  isFiltered?: boolean;
  onEdit: (resourceId: string) => void;
  onDelete: (resourceId: string) => void;
  isLoading?: boolean;
  maxHeight?: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
  onSort?: (field: string) => void;
};

export function ResourceTable({
  resources,
  totalCount,
  isFiltered = false,
  onEdit,
  onDelete,
  isLoading = false,
  maxHeight = 'calc(100vh - 250px)',
  sortBy,
  sortOrder,
  onSort,
}: ResourceTableProps) {
  // Show loading skeleton
  if (isLoading) {
    return <div data-testid="resource-table-skeleton"><ResourceTableSkeleton /></div>;
  }

  // Empty state
  if (resources.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <div className="text-gray-400 mb-2" data-testid="empty-state-illustration">
          <svg
            className="h-12 w-12 mx-auto"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            data-testid="empty-state-icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
            />
          </svg>
        </div>
        {isFiltered ? (
          <>
            <p className="text-gray-500 font-medium">No resources match your filters</p>
            <p className="text-gray-400 text-sm mt-1">
              Try adjusting your filters or search terms
            </p>
          </>
        ) : (
          <>
            <p className="text-gray-500 font-medium">No resources found</p>
            <p className="text-gray-400 text-sm mt-1">
              Get started by creating your first resource
            </p>
          </>
        )}
      </div>
    );
  }

  const TH_BASE = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider';

  const renderSortHeader = (label: string, field: string) => {
    const isActive = sortBy === field;
    return (
      <th
        className={`${TH_BASE} cursor-pointer select-none hover:text-gray-700 transition-colors`}
        onClick={() => onSort?.(field)}
      >
        <span className="inline-flex items-center gap-1">
          {label}
          {isActive && (sortOrder === 'asc'
            ? <ChevronUp className="h-3 w-3" />
            : <ChevronDown className="h-3 w-3" />)}
        </span>
      </th>
    );
  };

  const deriveStatus = (resource: AdminResource): string => {
    if (resource.status) return resource.status;
    if (resource.deletedAt) return 'archived';
    if (resource.publishedAt) return 'published';
    return 'draft';
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'published':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'draft':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'archived':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatStatus = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1);
  };

  return (
    <div className="flex flex-col border border-gray-200 rounded-md bg-white">
      {/* Sticky Header - Total count display */}
      <div className="sticky top-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-50 border-b border-gray-200">
        <div className="text-sm">
          {isFiltered ? (
            <span>
              Showing{' '}
              <span className="font-semibold text-gray-900">
                {resources.length.toLocaleString('en-US')}
              </span>
              {' '}of{' '}
              <span className="font-semibold text-gray-900">
                {totalCount.toLocaleString('en-US')}
              </span>
              {' '}resources{' '}
              <span className="text-[#026857] font-medium">(Filtered)</span>
            </span>
          ) : (
            <span>
              Total{' '}
              <span className="font-semibold text-gray-900">
                {totalCount.toLocaleString('en-US')}
              </span>
              {' '}
              {totalCount === 1 ? 'resource' : 'resources'}
            </span>
          )}
        </div>
      </div>

      {/* Scrollable table container */}
      <div 
        className="overflow-auto"
        style={{ maxHeight }}
      >
        <table className="w-full" role="table">
          <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
            <tr>
              {renderSortHeader('Title', 'title')}
              {renderSortHeader('Type', 'type')}
              {renderSortHeader('Status', 'status')}
              <th className={TH_BASE}>Author</th>
              {renderSortHeader('Created', 'createdAt')}
              {renderSortHeader('Updated', 'updatedAt')}
              <th className={`${TH_BASE} text-right`}>Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {resources.map((resource) => (
              <tr
                key={resource.id}
                className="hover:bg-gray-50 transition-colors"
                role="row"
              >
                {/* Title with featured indicator */}
                <td className="px-4 py-4">
                  <div className="flex items-start gap-2 max-w-xs">
                    {(resource.isFeatured || resource.isFeaturedForPro) && (
                      <Star
                        className="h-4 w-4 text-yellow-500 flex-shrink-0 mt-0.5"
                        fill="currentColor"
                        data-testid="featured-icon"
                      />
                    )}
                    <div className="text-sm font-medium text-gray-900 line-clamp-2">
                      {resource.title}
                    </div>
                  </div>
                </td>

                {/* Type badge */}
                <td className="px-4 py-4 whitespace-nowrap">
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                    {resource.type.name}
                  </span>
                </td>

                {/* Status badge */}
                <td className="px-4 py-4 whitespace-nowrap">
                  {(() => {
                    const status = deriveStatus(resource);
                    return (
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getStatusColor(status)}`}>
                        {formatStatus(status)}
                      </span>
                    );
                  })()}
                </td>

                {/* Author */}
                <td className="px-4 py-4 whitespace-nowrap">
                  <div className="text-sm text-gray-900">
                    {resource.author?.name || getAuthorProDisplayName(resource.authorPro) || '—'}
                  </div>
                </td>

                {/* Created */}
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(resource.createdAt)}
                </td>

                {/* Updated */}
                <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                  {formatDate(resource.updatedAt)}
                </td>

                {/* Actions */}
                <td className="px-4 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => onEdit(resource.id)}
                      className="p-1.5 text-gray-600 hover:text-[#026857] hover:bg-[#026857]/10 rounded-md transition-colors"
                      aria-label={`Edit ${resource.title}`}
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      onClick={() => onDelete(resource.id)}
                      className="p-1.5 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors"
                      aria-label={`Delete ${resource.title}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sticky Footer - Summary info */}
      <div className="sticky bottom-0 z-10 flex items-center justify-between px-4 py-2 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
        <div>
          {resources.length > 0 && (
            <span>
              Displaying {resources.length} {resources.length === 1 ? 'row' : 'rows'}
            </span>
          )}
        </div>
        <div className="flex items-center gap-4">
          <span>Scroll for more →</span>
        </div>
      </div>
    </div>
  );
}
